import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileCheck, 
  Download, 
  Shield, 
  Clock,
  AlertTriangle,
  CheckCircle2,
  Eye,
  Trash2,
  Loader2,
  X,
  Link as LinkIcon,
  Copy,
  ExternalLink
} from 'lucide-react';
import { useExchangeAuth, exchangeApi } from '../../contexts/ExchangeAuthContext';
import { toast } from 'sonner';

export const CredentialsList = ({ credentials, onRefresh, role = 'driver' }) => {
  const [selectedCredential, setSelectedCredential] = useState(null);
  const [showVerification, setShowVerification] = useState(null);
  const [verificationData, setVerificationData] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const handleVerify = async (credential) => {
    setVerifying(true);
    setShowVerification(credential.id);
    
    try {
      const res = await exchangeApi.verifyCredential(credential.id);
      setVerificationData(res.data);
    } catch (error) {
      console.error('Verification failed:', error);
      toast.error('Failed to verify credential');
    } finally {
      setVerifying(false);
    }
  };

  const handleDownload = async (credential) => {
    setDownloading(credential.id);
    
    try {
      const response = await exchangeApi.downloadCredentialFile(credential.id);
      
      // Create download link
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = credential.original_filename || `${credential.name}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Credential downloaded');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download credential');
    } finally {
      setDownloading(null);
    }
  };

  const copyHash = (hash) => {
    navigator.clipboard.writeText(hash);
    toast.success('Hash copied to clipboard');
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'verified':
        return { color: 'neon-green', icon: CheckCircle2, label: 'Verified' };
      case 'pending':
        return { color: 'blue', icon: Clock, label: 'Pending Review' };
      case 'expiring_soon':
        return { color: 'yellow', icon: AlertTriangle, label: 'Expiring Soon' };
      case 'expired':
        return { color: 'red', icon: AlertTriangle, label: 'Expired' };
      case 'rejected':
        return { color: 'red', icon: X, label: 'Rejected' };
      default:
        return { color: 'slate', icon: FileCheck, label: status };
    }
  };

  if (!credentials || credentials.length === 0) {
    return (
      <div className="text-center py-12">
        <FileCheck className="w-16 h-16 text-slate-600 mx-auto mb-4" />
        <h4 className="text-lg text-white font-medium mb-2">No Credentials</h4>
        <p className="text-slate-400">Upload your first credential to get started</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {credentials.map((credential) => {
        const statusConfig = getStatusConfig(credential.status);
        const StatusIcon = statusConfig.icon;
        
        return (
          <motion.div
            key={credential.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-4 hover:border-slate-600 transition-colors"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${statusConfig.color}-500/20`}>
                  <StatusIcon className={`w-6 h-6 text-${statusConfig.color}-400`} />
                </div>
                <div>
                  <h4 className="text-white font-medium">{credential.name}</h4>
                  <p className="text-sm text-slate-400 capitalize">
                    {credential.credential_type?.replace(/_/g, ' ')}
                  </p>
                  <div className="flex items-center space-x-4 mt-2 text-xs text-slate-500">
                    {credential.expiration_date && (
                      <span className={credential.status === 'expiring_soon' || credential.status === 'expired' ? 'text-yellow-400' : ''}>
                        Expires: {new Date(credential.expiration_date).toLocaleDateString()}
                      </span>
                    )}
                    {credential.file_size && (
                      <span>{(credential.file_size / 1024).toFixed(1)} KB</span>
                    )}
                    {credential.shared_with?.length > 0 && (
                      <span className="text-neon-cyan">
                        Shared with {credential.shared_with.length} org(s)
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <span className={`text-xs px-3 py-1 rounded-full bg-${statusConfig.color}-500/20 text-${statusConfig.color}-400`}>
                  {statusConfig.label}
                </span>
                
                {/* Blockchain verification badge */}
                {credential.blockchain_hash && (
                  <button
                    onClick={() => handleVerify(credential)}
                    className="p-2 text-neon-cyan hover:bg-neon-cyan/10 rounded-lg transition-colors"
                    title="Verify on blockchain"
                    data-testid={`verify-${credential.id}`}
                  >
                    <Shield className="w-4 h-4" />
                  </button>
                )}
                
                <button
                  onClick={() => handleDownload(credential)}
                  disabled={downloading === credential.id}
                  className="p-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50"
                  title="Download"
                  data-testid={`download-${credential.id}`}
                >
                  {downloading === credential.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                </button>
                
                <button
                  onClick={() => setSelectedCredential(credential)}
                  className="p-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                  title="View details"
                >
                  <Eye className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        );
      })}

      {/* Verification Modal */}
      <AnimatePresence>
        {showVerification && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => {
              setShowVerification(null);
              setVerificationData(null);
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-xl shadow-2xl overflow-hidden"
            >
              <div className="p-6 border-b border-slate-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-xl bg-neon-cyan/20 flex items-center justify-center">
                      <Shield className="w-5 h-5 text-neon-cyan" />
                    </div>
                    <h3 className="text-lg font-semibold text-white">Blockchain Verification</h3>
                  </div>
                  <button 
                    onClick={() => {
                      setShowVerification(null);
                      setVerificationData(null);
                    }}
                    className="text-slate-400 hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                {verifying ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <Loader2 className="w-10 h-10 text-neon-cyan animate-spin mb-4" />
                    <p className="text-slate-400">Verifying on blockchain...</p>
                  </div>
                ) : verificationData ? (
                  <div className="space-y-4">
                    {/* Verification Status */}
                    <div className={`p-4 rounded-lg ${
                      verificationData.verification?.verified 
                        ? 'bg-neon-green/10 border border-neon-green/30' 
                        : 'bg-red-500/10 border border-red-500/30'
                    }`}>
                      <div className="flex items-center space-x-3">
                        {verificationData.verification?.verified ? (
                          <CheckCircle2 className="w-6 h-6 text-neon-green" />
                        ) : (
                          <X className="w-6 h-6 text-red-400" />
                        )}
                        <div>
                          <p className={`font-medium ${
                            verificationData.verification?.verified ? 'text-neon-green' : 'text-red-400'
                          }`}>
                            {verificationData.verification?.verified ? 'Verified' : 'Not Verified'}
                          </p>
                          <p className="text-xs text-slate-400">
                            {verificationData.verification?.verified 
                              ? 'This document has been verified on the blockchain'
                              : verificationData.verification?.message || 'Verification failed'
                            }
                          </p>
                        </div>
                      </div>
                    </div>

                    {verificationData.verification?.verified && (
                      <>
                        {/* Block Hash */}
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">Block Hash</label>
                          <div className="flex items-center space-x-2 p-3 bg-white/5 rounded-lg">
                            <code className="flex-1 text-xs text-neon-cyan truncate font-mono">
                              {verificationData.verification?.block_hash}
                            </code>
                            <button
                              onClick={() => copyHash(verificationData.verification?.block_hash)}
                              className="text-slate-400 hover:text-white"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Document Hash */}
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">Document Hash (SHA-256)</label>
                          <div className="flex items-center space-x-2 p-3 bg-white/5 rounded-lg">
                            <code className="flex-1 text-xs text-slate-300 truncate font-mono">
                              {credentials.find(c => c.id === showVerification)?.file_hash}
                            </code>
                            <button
                              onClick={() => copyHash(credentials.find(c => c.id === showVerification)?.file_hash)}
                              className="text-slate-400 hover:text-white"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Timestamp */}
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">Recorded At</label>
                          <p className="text-sm text-white">
                            {verificationData.verification?.recorded_at 
                              ? new Date(verificationData.verification.recorded_at).toLocaleString()
                              : 'N/A'
                            }
                          </p>
                        </div>

                        {/* Chain Integrity */}
                        <div className="flex items-center space-x-2 p-3 bg-white/5 rounded-lg">
                          <LinkIcon className={`w-4 h-4 ${
                            verificationData.verification?.valid_chain ? 'text-neon-green' : 'text-yellow-400'
                          }`} />
                          <span className="text-sm text-slate-300">
                            Chain Integrity: {verificationData.verification?.valid_chain ? 'Valid' : 'Check Required'}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-400">
                    <Shield className="w-10 h-10 mx-auto mb-3 opacity-50" />
                    <p>Unable to load verification data</p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedCredential && (
          <CredentialDetailModal
            credential={selectedCredential}
            onClose={() => setSelectedCredential(null)}
            onDownload={() => handleDownload(selectedCredential)}
            downloading={downloading === selectedCredential.id}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const CredentialDetailModal = ({ credential, onClose, onDownload, downloading }) => {
  const statusConfig = {
    verified: { color: 'neon-green', label: 'Verified' },
    pending: { color: 'blue', label: 'Pending Review' },
    expiring_soon: { color: 'yellow', label: 'Expiring Soon' },
    expired: { color: 'red', label: 'Expired' },
    rejected: { color: 'red', label: 'Rejected' }
  }[credential.status] || { color: 'slate', label: credential.status };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-slate-900 border border-slate-700 rounded-xl shadow-2xl overflow-hidden"
      >
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`w-10 h-10 rounded-xl bg-${statusConfig.color}-500/20 flex items-center justify-center`}>
                <FileCheck className={`w-5 h-5 text-${statusConfig.color}-400`} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">{credential.name}</h3>
                <span className={`text-xs px-2 py-0.5 rounded-full bg-${statusConfig.color}-500/20 text-${statusConfig.color}-400`}>
                  {statusConfig.label}
                </span>
              </div>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Type</label>
              <p className="text-white capitalize">{credential.credential_type?.replace(/_/g, ' ')}</p>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">File Size</label>
              <p className="text-white">{credential.file_size ? `${(credential.file_size / 1024).toFixed(1)} KB` : 'N/A'}</p>
            </div>
            {credential.issue_date && (
              <div>
                <label className="block text-xs text-slate-500 mb-1">Issue Date</label>
                <p className="text-white">{new Date(credential.issue_date).toLocaleDateString()}</p>
              </div>
            )}
            {credential.expiration_date && (
              <div>
                <label className="block text-xs text-slate-500 mb-1">Expiration Date</label>
                <p className={credential.status === 'expiring_soon' || credential.status === 'expired' ? 'text-yellow-400' : 'text-white'}>
                  {new Date(credential.expiration_date).toLocaleDateString()}
                </p>
              </div>
            )}
            <div>
              <label className="block text-xs text-slate-500 mb-1">Uploaded</label>
              <p className="text-white">{new Date(credential.created_at).toLocaleDateString()}</p>
            </div>
            {credential.shared_with?.length > 0 && (
              <div>
                <label className="block text-xs text-slate-500 mb-1">Shared With</label>
                <p className="text-neon-cyan">{credential.shared_with.length} organization(s)</p>
              </div>
            )}
          </div>

          {credential.description && (
            <div>
              <label className="block text-xs text-slate-500 mb-1">Description</label>
              <p className="text-slate-300 text-sm">{credential.description}</p>
            </div>
          )}

          {credential.blockchain_hash && (
            <div className="p-4 bg-neon-cyan/10 border border-neon-cyan/30 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <Shield className="w-4 h-4 text-neon-cyan" />
                <span className="text-sm text-neon-cyan font-medium">Blockchain Verified</span>
              </div>
              <code className="text-xs text-slate-400 font-mono break-all">
                {credential.blockchain_hash}
              </code>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-slate-700 flex justify-end space-x-3">
          <button onClick={onClose} className="btn-secondary">
            Close
          </button>
          <button onClick={onDownload} disabled={downloading} className="btn-primary disabled:opacity-50">
            {downloading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Downloading...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Download
              </>
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default CredentialsList;
